import {
  Button,
  Grid,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";

import { useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import moment from "moment";
import {
  CCard,
  CCardBody,
  CCollapse,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CTable,
} from "@coreui/react";

import { treesByCompanyID } from "graphql/queries";
import { updateTree } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import CompanyBranchFilter from "components/companies/CompanyBranchFilter";
import { LayoutContext } from "layout/context/LayoutContext";
import { getCompanyName } from "graphql/custom-queries";
import ConfirmModal from "components/utils/ConfirmModal";
import ActionIcon from "components/actions/ActionIcon";
import TreeCreateForm from "./TreeCreateForm";
import TreeUpdateForm from "./TreeUpdateForm";

const Trees = () => {
  const { me } = useContext(LayoutContext);
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "agro:read",
    "agro:read",
    "agro:read",
  ]);
  const [trees, setTrees] = useState([]);
  const [treesLoading, setTreesLoading] = useState(false);
  const [treeDetails, setTreeDetails] = useState();
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    treeId: null,
    treeName: null,
  });
  const [modalTree, setModalTree] = useState({
    visible: false,
    treeId: null,
    action: null
  });
  const [searchBy, setSearchBy] = useState({
    company: {
      id: "",
      display: "",
    },
    branch: {
      id: "",
      display: "",
    },
  });
  const columnsMapped = useMemo(
    () =>
      trees.map((item) => ({
        ...item,
        companyName: item.companyName || <Loader />,
        actions: (
          <>
            <ActionIcon
              name={
                treeDetails?.id === item.id
                  ? strings.trees.hide
                  : strings.trees.show
              }
              icon={
                treeDetails?.id === item.id ? "VisibilityOff" : "Visibility"
              }
              color="primary"
              onClick={() => {
                treeDetails?.id === item.id
                  ? setTreeDetails(null)
                  : setTreeDetails(item);
              }}
            />
            {canUpdate && (
              <ActionIcon
                name={strings.trees.edit}
                icon="Edit"
                color="primary"
                onClick={() => openModal("update", item.id)}
              />
            )}
            {canDelete && (
              <ActionIcon
                name={strings.trees.delete}
                icon="Delete"
                color="red.60"
                onClick={() => {
                  setModalDetails({
                    visible: true,
                    treeId: item.id,
                    treeName: item.name,
                  });
                }}
              />
            )}
          </>
        ),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trees, treeDetails?.id]
  );

  const onNext = () => {
    fetchTrees(paginationTokens.nextToken);
  };
  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchTrees(previous, false);
  };
  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchTrees(undefined, undefined, keywordSplit);
  };
  const openModal = (action, id) =>
    setModalTree({ visible: true, action, treeId: id });
  const onSuccessTree = () => {
    setModalTree({ visible: false });
    fetchTrees();
  };

  async function fetchCompanyName(id) {
    try {
      const companyData = await API.graphql(
        graphqlOperation(getCompanyName, {
          id,
        })
      );
      const companyItem = companyData.data.getCompany;
      return companyItem?.name ?? "";
    } catch (err) {
      console.log("Error fetchCompanyName", err);
      return "";
    }
  }

  const getNamesByTrees = (list) => {
    list?.forEach(async (item) => {
      const companyName = await fetchCompanyName(item.companyID);

      setTrees((prevList) => {
        const updatedList = [...prevList];
        const index = updatedList.findIndex(
          (itemInd) => itemInd.id === item.id
        );

        if (index > -1) {
          updatedList[index] = {
            ...updatedList[index],
            companyName,
          };
        }

        return updatedList;
      });
    });
  };

  async function fetchTrees(token, isNext = true, keywords) {
    const nameOperation = treesByCompanyID;
    const nameOperationStr = "treesByCompanyID";
    try {
      setTreesLoading(true);

      if (!searchBy.company.id && !me.companyID) return;

      const treesData = await API.graphql(
        graphqlOperation(nameOperation, {
          companyID: searchBy.company.id || me.companyID,
          limit: !keywords ? 10 : undefined,
          nextToken: token,
          filter: keywords
            ? {
              and: keywords.map((key) => ({
                or: [
                  {
                    name: { contains: key },
                  },
                ],
              })),
              deletedAt: { attributeExists: false },
            }
            : { deletedAt: { attributeExists: false } },
        })
      );
      const treesItems = treesData.data[nameOperationStr].items;
      setTrees(treesItems);
      getNamesByTrees(treesItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: treesData.data[nameOperationStr].nextToken,
        };
      });
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      console.error(err);
    } finally {
      setTreesLoading(false);
    }
  }
  async function treeDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(updateTree, {
          input: { id, deletedAt: new Date().toISOString() },
        })
      );
      toast.success(strings.successDeleted);
      const deletedId = result.data.updateTree.id;
      setTrees(trees.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        treeId: null,
      });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchTrees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchBy.branch.id, searchBy.company.id]);

  const tableColumns = [
    {
      key: "companyName",
      label: strings.table.headers.companyName,
      _style: { width: "40%" },
    },
    {
      key: "name",
      label: strings.table.headers.name,
      _style: { width: "40%" },
    },
    {
      key: "actions",
      label: "",
      _style: { width: "20%" },
    },
  ];

  return (
    <div>
      <span className="fs-3">{strings.title}</span>
      {canCreate && (
        <Button
          style={styles.buttonAdd}
          children={strings.trees.addTree}
          variation="primary"
          onClick={() => openModal("create")}
        />
      )}
      <div className="p-3">
        <Grid
          className="mb-3"
          templateColumns={{ medium: "1fr", xl: "1fr 1fr 1fr" }}
          rowGap="15px"
          columnGap="15px"
        >
          <CompanyBranchFilter
            searchBy={searchBy}
            setSearchBy={setSearchBy}
            onlyCompany
          />
          <SearchField
            label={strings.table.search.label}
            placeholder={strings.table.search.placeholder}
            onSubmit={onKeywordSubmit}
            onClear={onKeywordSubmit}
          />
        </Grid>
        {treesLoading ? (
          <Loader />
        ) : (
          <>
            <CTable
              className="table-centered"
              items={columnsMapped}
              columns={tableColumns}
              tableHeadProps={{ color: "light" }}
              responsive
              hover
            />
            <Button
              size="small"
              disabled={paginationTokens.previous?.length <= 0}
              style={styles.buttonPrevious}
              onClick={onPrevious}
            >
              <FiArrowLeft /> {strings.table.pagination.buttonPrevious}
            </Button>
            <Button
              size="small"
              disabled={!paginationTokens.nextToken}
              style={styles.buttonNext}
              onClick={onNext}
            >
              <FiArrowRight /> {strings.table.pagination.buttonNext}
            </Button>
          </>
        )}
        <CCollapse visible={!!treeDetails?.id} style={styles.collapseCard}>
          <CCard key={treeDetails?.id}>
            <CCardBody>
              <h4 className="text-muted">
                {strings.details.title} "{treeDetails?.name}"
              </h4>

              <p className="text-muted">
                <b>{strings.details.name}:</b> {treeDetails?.name ?? "-"}
              </p>

              <p className="text-muted">
                <b>{strings.details.createdAt}:</b>{" "}
                {treeDetails?.createdAt
                  ? moment(treeDetails.createdAt).format(
                    "YYYY-MM-DD HH:mm:ss"
                  )
                  : "-"}
              </p>
            </CCardBody>
          </CCard>
        </CCollapse>
      </div>
      <ConfirmModal
        visible={modalDetails.visible}
        body={strings.formatString(strings.modal.confirmDeleteQuestion, {
          name: modalDetails.treeName,
        })}
        onCancel={() => setModalDetails({ visible: false, treeId: null })}
        onConfirm={() => treeDelete(modalDetails.treeId)}
      />
      {modalTree.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalTree.visible}
          onClose={() => setModalTree({ visible: false })}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalTree.action === "create" && strings.modalTree.addTitle}
              {modalTree.action === "update" && strings.modalTree.updateTitle}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalTree.action === "create" && (
                <TreeCreateForm onSuccess={onSuccessTree} />
              )}
              {modalTree.action === "update" && (
                <TreeUpdateForm
                  treeId={modalTree.treeId}
                  onSuccess={onSuccessTree}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </div>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Árboles",
    successDeleted: "Árbol eliminado con éxito",
    trees: {
      addTree: "Agregar Árbol",
      show: "Ver",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
    },
    responses: {
      errorFetch: "Error al consultar los árboles",
    },
    table: {
      headers: {
        name: "Nombre",
        companyName: "Empresa",
        actions: "Acciones",
      },
      pagination: {
        buttonPrevious: "Atrás",
        buttonNext: "Siguiente",
      },
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    details: {
      title: "Detalles del Árbol",
      name: "Nombre",
      createdAt: "Fecha de creación",
    },
    modal: {
      confirmDeleteQuestion: "¿Estás seguro de eliminar el árbol {name}?",
      cancelButton: "Cancelar",
      confirmButton: "Confirmar",
    },
    modalTree: {
      addTitle: "Agregar Árbol",
      updateTitle: "Actualizar Árbol",
    },
  },
});

const styles = {
  buttonAdd: { float: "right" },
  modal: { width: "250px" },
  collapseCard: { marginTop: "15px" },
  buttonPrevious: { marginLeft: "15px" },
  buttonNext: { float: "right", marginRight: "15px" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default Trees;
